import { isLineString, isPointOnLineString } from '@racemap/utilities/functions/validation';
import type { RacemapGeoObject } from '@racemap/utilities/types/geos';
import type { Feature } from 'geojson';
import { type Immutable, produce } from 'immer';
import type { LineStringCoordinate } from '../DirectSelectMode';
import { getNewCoordinateIndex } from './getNewCoordinateIndex';

interface Params {
  updatedFeatures: Immutable<Array<Feature & { id: string }>>;
  features: Immutable<Array<RacemapGeoObject>>;
  coordinates?: Array<LineStringCoordinate>;
  action: 'move' | 'change_coordinates';
  coordinateAction?: 'add' | 'delete' | 'move';
  onDelete?: (params: { features: Array<Feature & { id: string }> }) => void;
}

export function getUpdatedPointsOnLineString({
  updatedFeatures,
  features,
  coordinates,
  action,
  coordinateAction,
  onDelete,
}: Params): Immutable<Array<Feature & { id: string }>> {
  const modifiedLineString = new Map(updatedFeatures.filter(isLineString).map((f) => [f.id, f]));
  const affectedPointsOnLineString = features
    .filter(isPointOnLineString)
    .filter((f) => modifiedLineString.has(f.properties.lineStringId));

  return produce(updatedFeatures, (draft) => {
    for (const point of affectedPointsOnLineString) {
      // update the coordinates of the affected points
      const lineString = modifiedLineString.get(point.properties.lineStringId);
      const priviouseLineString = features
        .filter(isLineString)
        .find((f) => f.id === point.properties.lineStringId);
      if (lineString == null || priviouseLineString == null) continue;

      const corrospondingCoordinate = coordinates?.find(
        (c) => c.index === point.properties.coordinateIndex,
      );
      const firstAffectedCoordinateIndex = coordinates?.reduce(
        (pV, cV) => Math.min(pV, cV.index),
        Number.POSITIVE_INFINITY,
      );

      if (action === 'move' || (coordinateAction === 'move' && corrospondingCoordinate != null)) {
        // point or track was simple moved
        draft.push({
          ...point,
          geometry: {
            ...point.geometry,
            coordinates: lineString.geometry.coordinates[point.properties.coordinateIndex],
          },
        });
      } else if (
        (coordinateAction === 'add' || coordinateAction === 'delete') &&
        coordinates != null &&
        firstAffectedCoordinateIndex != null &&
        firstAffectedCoordinateIndex <= point.properties.coordinateIndex
      ) {
        // a new coordinate was created or removed before the point
        switch (coordinateAction) {
          case 'delete':
            // the coordinate of an point on linestring was removed
            // the corrosponding point will also removed
            if (corrospondingCoordinate != null) {
              onDelete?.({ features: [point] });
            }
            break;
          case 'add':
            draft.push({
              ...point,
              properties: {
                ...point.properties,
                coordinateIndex: getNewCoordinateIndex(
                  point,
                  lineString,
                  coordinates,
                  coordinateAction,
                ),
              },
            });
            break;
        }
      } else if (
        coordinateAction === 'add' &&
        coordinates != null &&
        point.properties.coordinateIndex === priviouseLineString.geometry.coordinates.length - 1 &&
        lineString.geometry.coordinates.length !== priviouseLineString.geometry.coordinates.length
      ) {
        // a new coordinate was created at the end of the lineString
        // the corrosponding finish point will also moved
        draft.push({
          ...point,
          geometry: {
            ...point.geometry,
            coordinates:
              lineString.geometry.coordinates[lineString.geometry.coordinates.length - 1],
          },
          properties: {
            ...point.properties,
            coordinateIndex: lineString.geometry.coordinates.length - 1,
          },
        });
      }
    }
  });
}
